import { createContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import {
  BundleHandlers,
  QuestionBundleDto,
  QuestionDto,
  QuestionHandlers,
  QuestionnaireDto,
} from '../Types/QuestionnaireT';
import { QuestionBundleS } from './QuestionBundleS';
import { QuestionnaireS } from './QuestionnaireS';

interface Props {
  loading: boolean;
  insertingBundle: boolean;
  questionnaire: QuestionnaireDto | null;
  patchQuestionnaire: (patch: Partial<QuestionnaireDto>) => Promise<boolean>;
  bundleHandlers: BundleHandlers;
  questionHandlers: QuestionHandlers;
}

export const QuestionnaireContext = createContext<Props>({} as Props);

export const QuestionnaireContextProvider = () => {
  const { qid } = useParams();
  const [loading, setLoading] = useState(false);
  const [insertingBundle, setInsertingBundle] = useState(false);
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireDto | null>(null);
  const { id: questionnaireId } = questionnaire ?? {};

  useEffect(() => {
    if (qid) {
      setLoading(true);
      QuestionnaireS.fetchQuestionnaire(qid)
        .then(setQuestionnaire)
        .finally(() => setLoading(false));
    }
  }, [qid]);

  const patchQuestionnaire = async (patch: Partial<QuestionnaireDto>) => {
    if (questionnaireId) {
      const resJSON = await QuestionnaireS.editQuestionnaire(questionnaireId, patch);
      if (resJSON) {
        setQuestionnaire(resJSON);
        return true;
      }
    }
    return false;
  };

  const insertQuestion = async (bundleId: string, index?: number) => {
    const resJSON = await QuestionBundleS.insertQuestion(bundleId, index);
    if (resJSON && questionnaire) {
      setQuestionnaire(QuestionBundleS.handleBundleChange(questionnaire, resJSON));
      return true;
    }
    return false;
  };

  const shiftQuestion = async (bundleId: string, questionId: string, shift: number) => {
    if (questionnaire) {
      const bundle = questionnaire.questionBundles.find((qb) => qb.id === bundleId);
      if (bundle) {
        const idx = bundle.questions.findIndex((q) => q.id === questionId);
        const targetQuestionId = bundle.questions[idx + shift].id;
        return moveQuestion(bundleId, questionId, targetQuestionId);
      }
    }
    return false;
  };

  const moveQuestion = async (
    bundleId: string,
    questionId: string,
    targetQuestionId: string,
    originBundleId?: string,
  ) => {
    if (originBundleId) setInsertingBundle(true);
    const resJSON = await QuestionBundleS.moveQuestion(bundleId, questionId, targetQuestionId, originBundleId);
    if (originBundleId) setInsertingBundle(false);
    if (resJSON && questionnaire) {
      setQuestionnaire(resJSON as QuestionnaireDto);
      return true;
    }
    return false;
  };

  const duplicateQuestion = async (bundleId: string, questionId: string, index?: number) => {
    const resJSON = await QuestionBundleS.duplicateQuestion(bundleId, questionId, index);
    if (resJSON && questionnaire) {
      setQuestionnaire(QuestionBundleS.handleQuestionDuplicate(questionnaire, bundleId, resJSON));
      return true;
    }
    return false;
  };

  const insertBundle = async (index?: number) => {
    if (questionnaireId) {
      setInsertingBundle(true);
      const resJSON = await QuestionnaireS.insertBundle(questionnaireId, index);
      setInsertingBundle(false);
      if (resJSON) {
        setQuestionnaire(resJSON);
        return true;
      }
    }
    return false;
  };

  const moveBundle = async (bundleId: string, targetBundleId: string) => {
    if (questionnaireId) {
      setInsertingBundle(true);
      const resJSON = await QuestionnaireS.moveBundle(questionnaireId, bundleId, targetBundleId);
      setInsertingBundle(false);
      if (resJSON) {
        setQuestionnaire(resJSON);
        return true;
      }
    }
    return false;
  };

  const deleteBundle = async (bundleId: string) => {
    if (questionnaireId) {
      const removed = await QuestionnaireS.removeBundle(questionnaireId, bundleId);
      if (removed && questionnaire) {
        setQuestionnaire(QuestionBundleS.handleBundleDelete(questionnaire, bundleId));
        return true;
      }
    }
    return false;
  };

  const editQuestion = async (questionId: string, patch: Partial<QuestionDto>) => {
    const resJSON = await QuestionBundleS.editQuestion(questionId, patch);
    if (resJSON && questionnaire) {
      setQuestionnaire(QuestionBundleS.handleQuestionChange(questionnaire, resJSON));
      return true;
    }
    return false;
  };

  const handleQuestionChange = (question: QuestionDto) => {
    if (questionnaire) {
      setQuestionnaire(QuestionBundleS.handleQuestionChange(questionnaire, question));
    }
  };

  const deleteQuestion = async (bundleId: string, questionId: string): Promise<boolean> => {
    const removed = await QuestionBundleS.deleteQuestion(bundleId, questionId);
    if (removed && questionnaire) {
      setQuestionnaire(QuestionBundleS.handleQuestionDelete(questionnaire, bundleId, questionId));
      return true;
    }
    return false;
  };

  const editBundle = async (bundleId: string, patch: Partial<QuestionBundleDto>) => {
    const resJSON = await QuestionBundleS.editBundle(bundleId, patch);
    if (resJSON && questionnaire) {
      setQuestionnaire(QuestionBundleS.handleBundleChange(questionnaire, resJSON));
      return true;
    }
    return false;
  };

  const value = useMemo(
    () => ({
      loading,
      insertingBundle,
      questionnaire,
      patchQuestionnaire,
      bundleHandlers: {
        insertBundle,
        editBundle,
        moveBundle,
        deleteBundle,
      },
      questionHandlers: {
        insertQuestion,
        moveQuestion,
        shiftQuestion,
        editQuestion,
        handleQuestionChange,
        duplicateQuestion,
        deleteQuestion,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, insertingBundle, questionnaire],
  );

  return (
    <QuestionnaireContext.Provider value={value}>
      <Outlet />
    </QuestionnaireContext.Provider>
  );
};
